import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const BulkGenerationLoading = ({ progress, totalEmails = 0, currentEmail = 0, onTransitionComplete }) => {
  const realProgress = Math.min(100, progress || 0);
  const [pseudoProgress, setPseudoProgress] = useState(0);
  const [dots, setDots] = useState('');
  const [phase, setPhase] = useState('loading');
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 600);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let value;
      if (elapsed < 30000) {
        const t = elapsed / 30000;
        value = (1 - Math.pow(1 - t, 2)) * 50;
      } else {
        const extra = (elapsed - 30000) / 60000;
        value = 50 + Math.log1p(extra * 4) * 15;
      }
      setPseudoProgress(Math.min(value, 85));
    }, 200);
    return () => clearInterval(interval);
  }, []);

  const adjustedProgress = Math.max(realProgress, pseudoProgress);

  const estimatedSeconds = Math.max(0, 5 - currentEmail) * 20;
  const timeDisplay = estimatedSeconds < 60
    ? `${estimatedSeconds}s`
    : `${Math.floor(estimatedSeconds / 60)}m ${estimatedSeconds % 60}s`;

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const popupContainer = document.querySelector('.emailAiPopupContainer');
    const popupContent = document.querySelector('.emailAiPopupContent');
    const origContainerOverflow = popupContainer?.style.overflow;
    const origContentOverflow = popupContent?.style.overflow;

    document.body.style.overflow = 'hidden';
    if (popupContainer) popupContainer.style.overflow = 'hidden';
    if (popupContent) popupContent.style.overflow = 'hidden';
    document.body.classList.add('bulk-generation-active');

    return () => {
      document.body.style.overflow = originalOverflow;
      if (popupContainer) popupContainer.style.overflow = origContainerOverflow;
      if (popupContent) popupContent.style.overflow = origContentOverflow;
      document.body.classList.remove('bulk-generation-active');
    };
  }, []);

  const isComplete = adjustedProgress >= 100;

  useEffect(() => {
    if (isComplete && phase === 'loading') {
      setPhase('ready');
    }
  }, [isComplete, phase]);

  useEffect(() => {
    if (phase === 'ready') {
      const timer = setTimeout(() => {
        setVisible(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  const handleExitComplete = useCallback(() => {
    onTransitionComplete?.();
  }, [onTransitionComplete]);

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {visible && (
        <motion.div
          className="bgl-overlay"
          key="bgl"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] }
          }}
        >
          <div className="bgl-blob"></div>
          <div className="bgl-blob"></div>
          <div className="bgl-blob"></div>
          <div className="bgl-shimmer"></div>

          <motion.div
            className="bgl-card"
            exit={{
              y: -50,
              scale: 0.92,
              opacity: 0,
              filter: 'blur(6px)',
              transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] }
            }}
          >
            {phase === 'loading' ? (
              <motion.div
                key="loading"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
                exit={{ opacity: 0, y: -15, transition: { duration: 0.25 } }}
              >
                <div className="bgl-logo-container">
                  <img src="/img/salesgod_dark.webp" alt="SalesGod" className="bgl-logo" />
                </div>
                <h2 className="bgl-title">{`Generating${dots}`}</h2>
                <p className="bgl-subtitle">{`Processing ${totalEmails} emails based on your templates.`}</p>
                <div className="bgl-stats">
                  <div className="bgl-stat">
                    <span className="bgl-stat-value">{currentEmail}<span className="bgl-stat-dim">/{totalEmails}</span></span>
                    <span className="bgl-stat-label">Emails</span>
                  </div>
                  <div className="bgl-stat-divider" />
                  <div className="bgl-stat">
                    <span className="bgl-stat-value">{Math.round(adjustedProgress)}<span className="bgl-stat-dim">%</span></span>
                    <span className="bgl-stat-label">Progress</span>
                  </div>
                  <div className="bgl-stat-divider" />
                  <div className="bgl-stat">
                    <span className="bgl-stat-value">{timeDisplay}</span>
                    <span className="bgl-stat-label">Remaining</span>
                  </div>
                </div>
                <div className="bgl-progress-track">
                  <div className="bgl-progress-fill" style={{ width: `${adjustedProgress}%` }} />
                </div>
                <p className="bgl-note">Once the first 5 are done, you can start working while the rest generates.</p>
              </motion.div>
            ) : (
              <motion.div
                key="ready"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0 20px' }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.1 }}
                >
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="30" stroke="var(--blue, #6897B7)" strokeWidth="2" fill="none" opacity="0.2" />
                    <circle cx="32" cy="32" r="30" stroke="var(--blue, #6897B7)" strokeWidth="2.5" fill="none"
                      strokeDasharray="188.5" strokeDashoffset="0"
                      style={{ filter: 'drop-shadow(0 0 8px rgba(104,151,183,0.3))' }}
                    />
                    <motion.path
                      d="M20 33L28 41L44 25"
                      stroke="var(--darkblue, #2a6776)"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                    />
                  </svg>
                </motion.div>

                <motion.h2
                  className="bgl-title"
                  style={{ marginTop: 24, fontSize: '28px' }}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.45, ease: [0, 0.7, 0.3, 1] }}
                >
                  Ready to go
                </motion.h2>

                <motion.p
                  className="bgl-subtitle"
                  style={{ marginTop: 8 }}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.4 }}
                >
                  {Math.min(5, totalEmails)} emails ready for review.
                </motion.p>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BulkGenerationLoading;
